import type { FormItem, InspectionFormItem } from './items';
import type * as Plugins from './plugins';

/** 模板。 */
export interface Form {
  /**
   * 多版本统一 ID。相同 id 的记录在业务上属于同一模板，
   * 不同版本拥有不同 _id。
   */
  id: string;

  /** 模板名称。 */
  name: string;

  /** 模板描述。 */
  description: string;

  /** 模板布局列数。 */
  column?: number;

  /** 存储表标识。 */
  schema: string;

  /** 是否公开。 */
  public?: boolean;

  /** 模板内嵌的题目/组件列表。 */
  items: FormItem[];

  /** 模板版本号，例如 1.0.0。 */
  ver: string;

  /** 是否为当前使用版本。 */
  active?: boolean;

  /** 表单业务类型。 */
  type: FormType;

  /** 统计方式：1=平均、2=合计、3=最低。 */
  scoreType: FormScoreType | '';

  /** 考试题库抽查题数。 */
  spotChecks?: number;

  /** 考试时长，单位为分钟。 */
  testTime?: number;

  /** 模板级插件和租户扩展配置。 */
  extend: Plugins.FormExtend;

  /**
   * 限定哪些组织内的门店可检查该模板。
   * 保存 group._id 的字符串数组；空数组表示未限定。
   */
  belongingOrgs: string[];

  /** 模板行号。 */
  row?: number;
}

/** 巡店模板。 */
export type InspectionForm<
  Customize extends Plugins.InspectionCustomizeCode | never = never,
> = Omit<
  Form,
  'type' | 'items' | 'extend' | 'spotChecks' | 'testTime'
> & {
  type: FormType.CHECK;
  items: InspectionFormItem[];
  extend: Plugins.InspectionFormExtend<Customize>;
  spotChecks?: never;
  testTime?: never;
};

/** 扫码问卷模板。 */
export type QuestionnaireForm = Omit<Form, 'type' | 'extend'> & {
  type: FormType.QUESTION;
  extend: Plugins.QuestionnaireFormExtend;
};

/** 考试题库模板。 */
export type ExaminationForm = Omit<Form, 'type' | 'extend'> & {
  type: FormType.ANSWER;
  extend: Plugins.ExaminationFormExtend;
};

/** 奖罚赔单模板。 */
export type TicketForm = Omit<Form, 'type' | 'extend'> & {
  type: FormType.TICKET;
  extend: Plugins.TicketFormExtend;
};

/** 表单类型。 */
export enum FormType {
  /** 扫码问卷。 */
  QUESTION = '1',
  /** APP 巡店表单。 */
  CHECK = '2',
  /** 考试题库。 */
  ANSWER = '3',
  /** 奖罚赔单。 */
  TICKET = '4',
}

/** 模板/报告统计方式。 */
export enum FormScoreType {
  /** 平均。 */
  AVERAGE = '1',
  /** 合计。 */
  TOTAL = '2',
  /** 最低。 */
  MIN = '3',
}

export interface CustomInputCell {
  placeholder?: string;
  empty?: string;
}

